/**
 * Post-deploy SPA chunk recovery.
 *
 * After a Static Web App deploy, hashed files under `/assets/*` are replaced.
 * Tabs that still run the previous entry bundle may then fail a `React.lazy`
 * / dynamic `import()` with "Failed to fetch dynamically imported module"
 * (or Vite's `vite:preloadError`). A single hard reload fetches the new
 * `index.html` (Cache-Control: no-cache) and recovers without a blank page.
 *
 * Cooldown prevents a genuine missing-asset bug from infinite-reloading.
 */

/** sessionStorage key: timestamp of the last stale-chunk auto-reload. */
export const STALE_DEPLOY_CHUNK_RELOAD_AT_KEY = 'ss.stale-deploy-chunk-reload-at';

/** Ignore a second auto-reload inside this window so a real 404 cannot loop. */
export const STALE_DEPLOY_CHUNK_RELOAD_COOLDOWN_MS = 10_000;

/**
 * Matches Vite / browser messages when a hashed chunk URL 404s after deploy.
 * Keep patterns specific so unrelated TypeErrors do not trigger a reload.
 */
export const STALE_DEPLOY_CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading chunk [\w-]+ failed|ChunkLoadError/i;

/**
 * Returns true when the error looks like a missing post-deploy asset chunk.
 */
export function isStaleDeployChunkError(error: unknown): boolean {
  if (error == null) {
    return false;
  }

  if (typeof error === 'string') {
    return STALE_DEPLOY_CHUNK_ERROR_PATTERN.test(error);
  }

  if (error instanceof Error) {
    if (STALE_DEPLOY_CHUNK_ERROR_PATTERN.test(error.message)) {
      return true;
    }

    // Some browsers put the failed URL only on the name / cause chain.
    if (error.name === 'ChunkLoadError') {
      return true;
    }

    if (error.cause != null && isStaleDeployChunkError(error.cause)) {
      return true;
    }
  }

  return false;
}

/**
 * Returns true when we should hard-reload for a stale chunk and have not
 * already reloaded within the cooldown window.
 */
export function shouldAutoReloadForStaleDeployChunk(error: unknown): boolean {
  if (!isStaleDeployChunkError(error)) {
    return false;
  }

  try {
    const lastRaw = sessionStorage.getItem(STALE_DEPLOY_CHUNK_RELOAD_AT_KEY);
    const lastAt = lastRaw ? Number(lastRaw) : 0;
    if (Number.isFinite(lastAt) && Date.now() - lastAt < STALE_DEPLOY_CHUNK_RELOAD_COOLDOWN_MS) {
      return false;
    }
  } catch {
    // sessionStorage may be blocked (private mode quirks); still allow one reload.
  }

  return true;
}

/**
 * Records the reload timestamp and reloads the page to pick up the new deploy.
 * Returns true when a reload was started.
 */
export function reloadForStaleDeployChunk(): boolean {
  try {
    sessionStorage.setItem(STALE_DEPLOY_CHUNK_RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // Proceed with reload even if storage write fails.
  }

  window.location.reload();
  return true;
}

/**
 * Registers Vite's `vite:preloadError` listener so failed CSS/JS preloads
 * after a deploy recover the same way as React.lazy failures.
 *
 * Call once at SPA bootstrap (before React mounts is ideal).
 */
export function registerVitePreloadErrorReload(): void {
  window.addEventListener('vite:preloadError', (event: Event) => {
    const preloadEvent = event as Event & { payload?: unknown };
    const payload = preloadEvent.payload;

    if (!shouldAutoReloadForStaleDeployChunk(payload ?? 'Unable to preload CSS')) {
      return;
    }

    // Prevent Vite's default unhandled rejection path when we own recovery.
    event.preventDefault();
    console.warn('[staleDeployChunkReload] vite:preloadError — reloading for new deploy');
    reloadForStaleDeployChunk();
  });
}
